import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  getEmployeeChangeHistory, getEmployeeChangeHistoryItem, employeeChangeHistoryExportUrl,
} from '../../../services/auditLogService';
import { formatDateTime } from '../../../utils/Utils';
import { buildReportLabels } from '../../../utils/reportLabels';
import DataTable from '../../../components/ui/DataTable';
import ReportExportButtons from '../../../components/ui/ReportExportButtons';
import AuditLogDetailDrawer from '../../audit-logs/AuditLogDetailDrawer';
import { useAuditLabels, auditExportLabels, auditFieldExportLabels, StaffBadge } from '../../audit-logs/config';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ErrorNote = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #991B1B;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  margin-bottom: 12px;
`;

/**
 * AKM POC §9.1 — çalışan kartının Değişiklik Geçmişi (EM.Write): kişinin veri ve yetki değişiklikleri, en yeni önce; sunucu
 * kayıt türünü modül yetkisine göre süzer. Satır → İşlem Geçmişi ile aynı salt-okunur ayrıntı çekmecesi, sekmenin kendi
 * ayrıntı ucuyla. Alan adları insan etiketiyle (çevirisi olmayan ham ad). Dışa aktarma sunucudan (Excel / PDF); yerleşik CSV yok.
 */
export default function EmployeeChangeHistoryTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const labels = useAuditLabels();
  const [params, setParams] = useState({ pageNumber: 1, pageSize: 20 });
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 20, pageIndex: 0 });
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    setFetching(true);
    getEmployeeChangeHistory(employeeId, params)
      .then((res) => {
        if (!res?.data || res.data.status === false) {
          setLoadFailed(true);
          setData([]);
          return;
        }
        const result = res.data.data || {};
        const items = (Array.isArray(result) ? result : (result.data || []))
          .map((r) => ({ ...r, changes: (r.changedFields || []).join(', ') }));
        setLoadFailed(false);
        setData(items);
        setPagination({
          totalRowCount: result.totalRowCount ?? items.length,
          pageIndex: result.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result.pageSize || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [employeeId, params]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'occurredAt',
      label: t('auditLog.occurredAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDateTime(v, i18n.language) },
    },
    { name: 'action', label: t('auditLog.action'), options: { filter: false, sort: false, customBodyRender: (v) => labels.action(v) } },
    {
      name: 'entityType',
      label: t('auditLog.entityType'),
      options: { filter: false, sort: false, customBodyRender: (v) => labels.entityType(v) || '-' },
    },
    {
      name: 'actorName',
      label: t('auditLog.changedBy'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => (data[tableMeta.rowIndex]?.actorIsSuperAdmin
          ? <StaffBadge>{t('auditLog.flexbeeStaff')}</StaffBadge>
          : v || '-'),
      },
    },
    {
      name: 'changes',
      label: t('auditLog.changedFields'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => (data[tableMeta.rowIndex]?.changedFields || []).map(labels.field).join(', ') || '-',
      },
    },
  ], [t, i18n.language, labels, data]);

  const handleTableChanged = useCallback((action, tableState) => {
    if (action !== 'changePage' && action !== 'changeRowsPerPage') return;
    setParams({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || 20 });
  }, []);

  const getExportLabels = useCallback(
    () => buildReportLabels(t, columns, t('employee.changeHistory'), {
      ...auditExportLabels(t, labels),
      ...auditFieldExportLabels(labels),
      employee: t('auditLog.subject'),
    }),
    [t, columns, labels],
  );

  const fetchDetail = useCallback((id) => getEmployeeChangeHistoryItem(employeeId, id), [employeeId]);

  const toolbarActions = useMemo(() => (
    <ReportExportButtons url={employeeChangeHistoryExportUrl(employeeId)} getLabels={getExportLabels} />
  ), [employeeId, getExportLabels]);

  return (
    <Container>
      <AuditLogDetailDrawer logId={selectedId} onClose={() => setSelectedId(null)} fetchDetail={fetchDetail} />
      {loadFailed && <ErrorNote role="alert">{t('auditLog.loadFailed')}</ErrorNote>}
      <DataTable
        title={t('employee.changeHistory')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onTableChanged={handleTableChanged}
        onRowClick={(row) => setSelectedId(row.id)}
        toolbarActions={toolbarActions}
        disableFilters
        download={false}
      />
    </Container>
  );
}
